import { Card, CardContent, CardHeader, Input, ScrollArea } from '@ibsys/kit';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  useExpertReport,
  useUpdateExpertReport,
  flattenBlocks,
  mapRules,
  toPredicateInput,
  type BlockResponse,
  type RuleInBlockUpdate,
  type RuleResponse,
} from '@/entities/expert-report';
import { useHasPermission } from '@/entities/profile';
import { toastWarning } from '@/shared/lib';
import { ConfirmationButton, SaveButton } from '@/shared/ui';
import { BlockPointsField } from './BlockPointsField';
import { RuleList } from './RuleList';
import { type BlockCardFormValues, type EditableRule } from './types';

type BlockCardProps = {
  reportId: number;
  blockId: number;
  onBlockDeleted: () => void;
  className?: string;
};

/** Рекурсивно ищет блок в дереве по идентификатору */
const findBlock = (blocks: BlockResponse[], blockId: number): BlockResponse | undefined => {
  for (const block of blocks) {
    if (block.id === blockId) return block;
    const found = findBlock(block.children, blockId);
    if (found) return found;
  }
  return undefined;
};

/** Собирает список сохраняемых правил из отредактированных в форме */
const buildRuleUpdates = (editedRules: EditableRule[]): RuleInBlockUpdate[] =>
  editedRules.map((r, index) => ({
    id: r.id,
    name: r.name,
    text: r.text,
    orderPosition: index,
    isDeleted: false,
    predicate: r.predicate ? { dsl: r.predicate.dsl, dslHuman: r.predicate.dslHuman } : undefined,
    knowledgeSetIds: r.knowledgeSets.map((ks) => ks.id),
  }));

/** Собирает список удалённых правил: были у блока, но убраны из формы */
const buildDeletedRules = (editedRules: EditableRule[], originalRules: RuleResponse[]): RuleInBlockUpdate[] => {
  const editedRuleIds = new Set(editedRules.filter((r) => r.id != null).map((r) => r.id!));
  return originalRules
    .filter((or) => !editedRuleIds.has(or.id))
    .map((or) => ({
      ...mapRules([or])[0],
      isDeleted: true,
    }));
};

const mapBlockToFormRules = (block: BlockResponse): EditableRule[] =>
  block.rules
    .filter((r) => !r.isDeleted)
    .map((r) => ({
      id: r.id,
      name: r.name,
      text: r.text,
      predicate: r.predicate ? { dsl: r.predicate.dsl ?? '', dslHuman: r.predicate.dslHuman ?? '' } : undefined,
      knowledgeSets: r.knowledgeSets ?? [],
    }));

export const BlockCard: FC<BlockCardProps> = ({ reportId, blockId, onBlockDeleted, className }) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const { data: report, isLoading } = useExpertReport(reportId);
  const { mutateAsync: updateReport, isPending: isUpdating } = useUpdateExpertReport();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Баллы в прошлом сохранении определить не удалось — даём повторить попытку без правок в карточке
  const [hasPointsFailure, setHasPointsFailure] = useState(false);

  const block = useMemo(() => {
    if (!report) return undefined;
    return findBlock(report.blocks, blockId);
  }, [report, blockId]);

  const form = useForm<BlockCardFormValues>({
    defaultValues: {
      name: block?.name ?? '',
      rules: block ? mapBlockToFormRules(block) : [],
    },
  });

  const { control, reset, handleSubmit, formState } = form;

  useEffect(() => {
    if (block) {
      reset({
        name: block.name,
        rules: mapBlockToFormRules(block),
      });
    }
  }, [block, reset]);

  const isLeafBlock = useMemo(() => (block ? block.children.filter((c) => !c.isDeleted).length === 0 : false), [block]);

  const focusNameInput = useCallback(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, []);

  // При переходе к другому блоку фокус переносим на поле названия, а неудачу расчёта
  // прошлого блока забываем — она относится только к нему
  useEffect(() => {
    setHasPointsFailure(false);
    const timer = setTimeout(focusNameInput, 100);
    return () => clearTimeout(timer);
  }, [blockId, focusNameInput]);

  const onSubmit = async (data: BlockCardFormValues) => {
    if (!report) return;

    const originalRules = block?.rules.filter((r) => !r.isDeleted) ?? [];

    const updatedReport = await updateReport({
      id: reportId,
      body: {
        name: report.name,
        predicate: toPredicateInput(report.predicate),
        // Баллы определяются только сохраняемому блоку — остальные блоки заключения не трогаем
        pointsBlockId: blockId,
        blocks: flattenBlocks(report.blocks, null, (b) => {
          if (b.id !== blockId) return {};

          const updatedRules = buildRuleUpdates(data.rules);
          const deletedRules = buildDeletedRules(data.rules, originalRules);

          return { name: data.name.trim(), rules: [...updatedRules, ...deletedRules] };
        }),
      },
    });

    // Сохранение остаётся в силе, даже если баллы определить не удалось, — предупреждаем отдельно
    const pointsError = findBlock(updatedReport.blocks, blockId)?.pointsError;
    setHasPointsFailure(Boolean(pointsError));
    if (pointsError) {
      toastWarning(t('expertReport:pointsResolveFailed'), { description: pointsError });
    }
  };

  const handleDelete = async () => {
    if (!report) return;
    await updateReport({
      id: reportId,
      body: {
        name: report.name,
        predicate: toPredicateInput(report.predicate),
        blocks: flattenBlocks(report.blocks, null, (block) => (block.id === blockId ? { isDeleted: true } : {})),
      },
    });
    onBlockDeleted();
  };

  if (isLoading) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('common:loading')}</div>
        </CardHeader>
      </Card>
    );
  }

  if (!block) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('expertReport:selectItem')}</div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <FormProvider {...form}>
      <Card className={className}>
        <CardHeader className="space-y-4">
          <Input
            ref={nameInputRef}
            control={control}
            name="name"
            label={t('expertReport:blockName')}
            disabled={!allowAdmin}
            rules={{
              required: { value: true, message: t('common:required') },
              // Имя из одних пробелов required не отсекает, а сохранять блок с таким именем нельзя
              validate: (value) => (typeof value === 'string' && value.trim().length > 0) || t('common:required'),
            }}
          />

          {isLeafBlock && (
            <BlockPointsField
              maxPoints={block.maxPoints}
              possiblePoints={block.possiblePoints}
              isProcessing={isUpdating}
            />
          )}

          {allowAdmin && (
            <SaveButton
              className="justify-end"
              isDirty={formState.isDirty}
              allowSaveWithoutChanges={hasPointsFailure}
              isLoading={isUpdating}
              onClick={handleSubmit(onSubmit)}
              secondaryAction={
                <ConfirmationButton
                  onConfirm={handleDelete}
                  isLoadingConfirm={isUpdating}
                  title={t('expertReport:removeBlock')}
                  description={
                    <Trans
                      i18nKey="expertReport:removeBlockDescription"
                      values={{ blockName: block.name }}
                      components={{ strong: <strong /> }}
                    />
                  }
                  trigger={{
                    variant: 'outline',
                    color: 'destructive',
                    children: t('expertReport:deleteBlock'),
                  }}
                />
              }
            />
          )}
        </CardHeader>

        <CardContent className="space-y-4 min-h-0 overflow-hidden">
          <ScrollArea className={'h-full'}>
            <RuleList isLeafBlock={isLeafBlock} isUpdating={isUpdating} />
          </ScrollArea>
        </CardContent>
      </Card>
    </FormProvider>
  );
};
